'use client';

/**
 * BossCallout — the BIG "your input is needed" window.
 *
 * Boss (2026-09-02): "I can be working on something else — when my input is
 * needed, I need a big approval window show up on my screen in the beast."
 *
 * Polls /api/pipeline/live (cheap fs-read batch route — see the freeze scar in
 * useLiveRuns). When any run turns 'waiting', a centered modal takes the screen
 * with one button per waiting project → jumps to the factory Run tab.
 *
 * NEVER traps: backdrop click, Esc, and ✕ all dismiss. Dismissal is per
 * ask-signature (slug+step), so the SAME question never re-pops after "later",
 * but a NEW question does. Telegram delivery is handled server-side by
 * run-loop's notify_boss — this is the on-screen half.
 */
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';

interface Waiting { slug: string; step: string; ask?: string }

export default function BossCallout() {
  const [waiting, setWaiting] = useState<Waiting[]>([]);
  // Dismissals persist in sessionStorage — in-memory state reset on every
  // navigation, so the SAME question re-popped on each page change and Boss
  // rightly lost his temper (2026-09-03). Session-scoped on purpose: a fresh
  // app launch may re-surface still-pending questions.
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('boss-callout-dismissed') || '[]')); }
    catch { return new Set(); }
  });
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    let stop = false, inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await fetch('/api/pipeline/live').then(r => r.json());
        if (!stop && d?.ok) {
          const w: Waiting[] = Object.entries(d.live || {})
            .filter(([, v]: [string, any]) => v.state === 'waiting')
            .map(([slug, v]: [string, any]) => ({ slug, step: v.step, ask: v.ask }));
          setWaiting(w);
        }
      } catch { /* poll again next tick */ }
      inFlight = false;
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const sig = (w: Waiting) => `${w.slug}:${w.step}`;
  const fresh = waiting.filter(w => !dismissed.has(sig(w)));
  // The factory page already shows asks inline — don't double-cover it.
  const open = fresh.length > 0 && pathname !== '/factory';

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismissAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fresh.map(sig).join('|')]);

  const dismissAll = () =>
    setDismissed(prev => {
      const next = new Set([...prev, ...fresh.map(sig)]);
      try { sessionStorage.setItem('boss-callout-dismissed', JSON.stringify([...next])); } catch { /* private mode */ }
      return next;
    });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm"
         onClick={dismissAll}>
      <div onClick={e => e.stopPropagation()}
           className="w-[min(560px,92vw)] rounded-2xl bg-[#12151c] ring-2 ring-amber-500/50
                      shadow-[0_0_80px_rgba(245,158,11,0.25)] p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-amber-400 text-lg font-semibold">
              🟡 Boss — your input is needed
            </div>
            <div className="text-gray-400 text-xs mt-1">
              {fresh.length === 1 ? 'One run is' : `${fresh.length} runs are`} paused waiting for you.
              Everything else keeps walking.
            </div>
          </div>
          <button onClick={dismissAll} aria-label="Dismiss"
                  className="text-gray-500 hover:text-gray-200 text-xl leading-none px-1">×</button>
        </div>

        <div className="space-y-2">
          {fresh.map(w => (
            <button key={sig(w)}
              onClick={() => { dismissAll(); router.push('/factory'); }}
              className="w-full flex items-center justify-between rounded-xl bg-amber-500/10
                         ring-1 ring-amber-500/30 hover:bg-amber-500/20 px-4 py-3 text-left">
              <div>
                <div className="text-amber-200 text-sm font-medium">{w.slug}</div>
                <div className="text-gray-500 text-[11px] font-mono mt-0.5">
                  waiting at {w.step}{w.ask ? ` · ${w.ask}` : ''}
                </div>
              </div>
              <span className="text-amber-300 text-xs shrink-0 ml-3">Answer →</span>
            </button>
          ))}
        </div>

        <div className="text-[11px] text-gray-600">
          Esc / click outside = later. This exact question won&apos;t pop again; a new one will.
          A Telegram ping was also sent.
        </div>
      </div>
    </div>
  );
}
